/**
 * Hook pour gérer les tests de commandes
 * @module admin/hooks/useCommandTest
 */

import { useState, useEffect, useCallback } from 'react';
import { getCommands, testCommand, resetLimit } from '../services/commandTestService';

const DEFAULT_TEST_OPTIONS = {
  username: 'test_user',
  displayName: 'Test User',
  isMod: false,
  isBroadcaster: true,
  isVIP: false,
  isSubscriber: false,
};

const HISTORY_STORAGE_KEY = 'commandTestHistory';
const MAX_HISTORY_ENTRIES = 50;

/**
 * Parse command arguments string into an array
 * @param {string} argsString - Raw arguments string
 * @returns {string[]} Parsed arguments array
 */
const parseCommandArgs = (argsString) => {
  const trimmed = argsString.trim();
  return trimmed ? trimmed.split(/\s+/).filter((arg) => arg.length > 0) : [];
};

/**
 * Load history from localStorage
 * @returns {Array} Parsed history or empty array
 */
const loadHistoryFromStorage = () => {
  try {
    const savedHistory = localStorage.getItem(HISTORY_STORAGE_KEY);
    return savedHistory ? JSON.parse(savedHistory) : [];
  } catch (err) {
    console.error('Erreur lors du chargement de l\'historique:', err);
    return [];
  }
};

/**
 * Save history to localStorage
 * @param {Array} history - History entries to save
 */
const saveHistoryToStorage = (history) => {
  localStorage.setItem(
    HISTORY_STORAGE_KEY,
    JSON.stringify(history.slice(0, MAX_HISTORY_ENTRIES))
  );
};

/**
 * Hook de gestion des tests de commandes
 * @returns {Object} État et fonctions pour les tests de commandes
 */
export const useCommandTest = () => {
  const [commands, setCommands] = useState([]);
  const [selectedCommand, setSelectedCommand] = useState('');
  const [commandArgs, setCommandArgs] = useState('');
  const [testOptions, setTestOptions] = useState(DEFAULT_TEST_OPTIONS);
  const [isLoading, setIsLoading] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);

  /**
   * Récupère la liste des commandes
   */
  const fetchCommands = useCallback(async () => {
    try {
      const data = await getCommands();
      if (data.success) {
        setCommands(data.commands);
      }
    } catch (err) {
      console.error('Erreur lors de la récupération des commandes:', err);
      setError('Erreur lors de la récupération des commandes');
    }
  }, []);

  /**
   * Réinitialise l'état de sélection
   */
  const resetSelectionState = useCallback(() => {
    setCommandArgs('');
    setResult(null);
    setError(null);
  }, []);

  /**
   * Gère le changement de commande sélectionnée
   * @param {string} commandName - Nom de la commande
   */
  const handleCommandChange = useCallback((commandName) => {
    setSelectedCommand(commandName);
    resetSelectionState();
  }, [resetSelectionState]);

  /**
   * Gère le changement des arguments
   * @param {string} args - Arguments de la commande
   */
  const handleArgsChange = useCallback((args) => {
    setCommandArgs(args);
  }, []);

  /**
   * Gère le changement d'une option de test
   * @param {string} optionName - Nom de l'option
   * @param {*} value - Nouvelle valeur
   */
  const handleOptionChange = useCallback((optionName, value) => {
    setTestOptions((prev) => ({
      ...prev,
      [optionName]: value,
    }));
  }, []);

  /**
   * Ajoute une entrée à l'historique
   * @param {Object} entry - Entrée à ajouter
   */
  const addToHistory = useCallback((commandName, args, options, data) => {
    const historyEntry = {
      id: Date.now(),
      commandName,
      args,
      options: { ...options },
      result: data,
      timestamp: new Date().toISOString(),
    };
    setHistory((prev) => [historyEntry, ...prev].slice(0, MAX_HISTORY_ENTRIES));
  }, []);

  /**
   * Teste l'exécution d'une commande
   */
  const handleTestCommand = useCallback(async () => {
    if (!selectedCommand) {
      setError('Veuillez sélectionner une commande');
      return;
    }

    setIsLoading(true);
    setResult(null);
    setError(null);

    try {
      const args = parseCommandArgs(commandArgs);
      const data = await testCommand(selectedCommand, args, testOptions);

      setResult(data);
      addToHistory(selectedCommand, args, testOptions, data);
    } catch (err) {
      setError(err.message || 'Erreur lors du test de la commande');
    } finally {
      setIsLoading(false);
    }
  }, [selectedCommand, commandArgs, testOptions, addToHistory]);

  /**
   * Rejoue un test depuis l'historique
   * @param {Object} entry - Entrée de l'historique
   */
  const replayTest = useCallback((entry) => {
    setSelectedCommand(entry.commandName);
    setCommandArgs(entry.args.join(' '));
    setTestOptions(entry.options);
  }, []);

  /**
   * Supprime une entrée de l'historique
   * @param {number} id - ID de l'entrée
   */
  const deleteHistoryEntry = useCallback((id) => {
    setHistory((prev) => prev.filter((entry) => entry.id !== id));
  }, []);

  /**
   * Efface tout l'historique
   */
  const clearHistory = useCallback(() => {
    setHistory([]);
    localStorage.removeItem(HISTORY_STORAGE_KEY);
  }, []);

  /**
   * Réinitialise le compteur de limite pour la commande sélectionnée
   */
  const handleResetLimit = useCallback(async () => {
    if (!selectedCommand) {
      setError('Veuillez sélectionner une commande');
      return;
    }

    setIsResetting(true);
    setError(null);

    try {
      const data = await resetLimit(selectedCommand);
      if (data.success) {
        await fetchCommands();
        setResult(null);
      }
    } catch (err) {
      setError(err.message || 'Erreur lors de la réinitialisation de la limite');
    } finally {
      setIsResetting(false);
    }
  }, [selectedCommand, fetchCommands]);

  // Charger les commandes au montage
  useEffect(() => {
    fetchCommands();
  }, [fetchCommands]);

  // Charger l'historique depuis localStorage
  useEffect(() => {
    setHistory(loadHistoryFromStorage());
  }, []);

  // Sauvegarder l'historique dans localStorage
  useEffect(() => {
    if (history.length > 0) {
      saveHistoryToStorage(history);
    }
  }, [history]);

  return {
    commands,
    selectedCommand,
    commandArgs,
    testOptions,
    isLoading,
    isResetting,
    result,
    error,
    history,
    fetchCommands,
    handleCommandChange,
    handleArgsChange,
    handleOptionChange,
    handleTestCommand,
    handleResetLimit,
    replayTest,
    deleteHistoryEntry,
    clearHistory,
  };
};
